/* eslint-disable no-unused-vars */
import React, { useState, useEffect, useCallback } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  IconButton,
  Typography,
  Box,
  Chip,
  Divider,
  CircularProgress,
  Alert,
  Stack,
  Tooltip,
} from "@mui/material";
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  FlashOn as FlashOnIcon,
  Close as CloseIcon,
  Refresh as RefreshIcon,
} from "@mui/icons-material";
import { toast } from "react-toastify";
import api from "../../services/api";
import CampaignModalPhrase from "../CampaignModalPhrase";
import ConfirmationModal from "../ConfirmationModal";

const FlowTriggersModal = ({ open, onClose, flowId, flowName }) => {
  const [triggers, setTriggers] = useState([]);
  const [loading, setLoading] = useState(false);

  // Sub-modal: criar/editar gatilho
  const [phraseModalOpen, setPhraseModalOpen] = useState(false);
  const [editingCampaignId, setEditingCampaignId] = useState(null);

  // Sub-modal: confirmar deleção
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [deletingId, setDeletingId] = useState(null);

  const fetchTriggers = useCallback(async () => {
    if (!flowId) return;
    setLoading(true);
    try {
      const response = await api.get("/flowcampaign");
      // Compatibilidade com diferentes formatos de resposta
      const all =
        response.data?.flow ||
        response.data?.data ||
        (Array.isArray(response.data) ? response.data : []);
      // Filtrar pelo flowId atual
      const filtered = all.filter((c) => Number(c.flowId) === Number(flowId));
      setTriggers(filtered);
    } catch (error) {
      console.error("Erro ao buscar gatilhos:", error);
      toast.error("Erro ao carregar gatilhos");
    } finally {
      setLoading(false);
    }
  }, [flowId]);

  useEffect(() => {
    if (open) fetchTriggers();
  }, [open, fetchTriggers]);

  const handleNewTrigger = () => {
    setEditingCampaignId(null);
    setPhraseModalOpen(true);
  };

  const handleEditTrigger = (id) => {
    setEditingCampaignId(id);
    setPhraseModalOpen(true);
  };

  const handleDeleteClick = (id) => {
    setDeletingId(id);
    setConfirmOpen(true);
  };

  const handleDeleteConfirm = async () => {
    try {
      await api.delete(`/flowcampaign/${deletingId}`);
      toast.success("Gatilho removido com sucesso!");
      fetchTriggers();
    } catch (error) {
      console.error("Erro ao deletar gatilho:", error);
      toast.error("Erro ao remover gatilho");
    } finally {
      setDeletingId(null);
      setConfirmOpen(false);
    }
  };

  const handlePhraseSave = () => {
    setPhraseModalOpen(false);
    fetchTriggers();
  };

  const handlePhraseClose = () => {
    setPhraseModalOpen(false);
    setEditingCampaignId(null);
  };

  return (
    <>
      <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm" scroll="paper">
        <DialogTitle>
          <Box display="flex" alignItems="center" justifyContent="space-between">
            <Box display="flex" alignItems="center" gap={1}>
              <FlashOnIcon color="warning" />
              <Typography variant="h6" component="span">
                Gatilhos do fluxo
              </Typography>
            </Box>
            <Box display="flex" alignItems="center" gap={0.5}>
              <Tooltip title="Atualizar lista">
                <IconButton size="small" onClick={fetchTriggers} disabled={loading}>
                  <RefreshIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <IconButton size="small" onClick={onClose}>
                <CloseIcon fontSize="small" />
              </IconButton>
            </Box>
          </Box>
          {flowName && (
            <Typography variant="body2" color="textSecondary" sx={{ mt: 0.5 }}>
              Fluxo: <strong>{flowName}</strong>
            </Typography>
          )}
        </DialogTitle>

        <Divider />

        <DialogContent sx={{ p: 0, minHeight: 200 }}>
          {loading ? (
            <Box display="flex" justifyContent="center" alignItems="center" py={6}>
              <CircularProgress size={32} />
            </Box>
          ) : triggers.length === 0 ? (
            <Box py={5} px={3} textAlign="center">
              <FlashOnIcon sx={{ fontSize: 48, color: "text.disabled", mb: 1 }} />
              <Typography variant="body1" color="textSecondary" gutterBottom>
                Nenhum gatilho configurado
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Adicione frases ou palavras-chave que disparam este fluxo automaticamente.
              </Typography>
            </Box>
          ) : (
            <Stack divider={<Divider />}>
              {triggers.map((trigger) => {
                const phrases = Array.isArray(trigger.phrase) ? trigger.phrase : [];
                const whatsappCount = Array.isArray(trigger.whatsappIds)
                  ? trigger.whatsappIds.length
                  : trigger.whatsappId
                  ? 1
                  : 0;

                return (
                  <Box
                    key={trigger.id}
                    sx={{
                      px: 3,
                      py: 2,
                      "&:hover": { backgroundColor: "rgba(0,0,0,0.02)" },
                    }}
                  >
                    <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                      <Box flex={1} mr={1}>
                        <Box display="flex" alignItems="center" gap={1} mb={0.5}>
                          <Typography variant="subtitle2" fontWeight={600}>
                            {trigger.name}
                          </Typography>
                          <Chip
                            label={trigger.status !== false ? "Ativo" : "Inativo"}
                            size="small"
                            color={trigger.status !== false ? "success" : "default"}
                            sx={{ height: 20, fontSize: 11 }}
                          />
                        </Box>

                        {/* Frases */}
                        <Box display="flex" flexWrap="wrap" gap={0.5} mb={0.5}>
                          {phrases.slice(0, 4).map((p, i) => (
                            <Chip
                              key={i}
                              label={`${p.text} (${p.type === "exact" ? "exata" : "parcial"})`}
                              size="small"
                              variant="outlined"
                              sx={{ fontSize: 11, maxWidth: 180 }}
                            />
                          ))}
                          {phrases.length > 4 && (
                            <Chip
                              label={`+${phrases.length - 4} frases`}
                              size="small"
                              variant="outlined"
                              sx={{ fontSize: 11 }}
                            />
                          )}
                        </Box>

                        {/* Conexões */}
                        <Typography variant="caption" color="textSecondary">
                          {whatsappCount === 0
                            ? "Sem conexões"
                            : `${whatsappCount} conexão(ões) WhatsApp`}
                          {" · "}
                          {phrases.length} frase{phrases.length !== 1 ? "s" : ""}
                        </Typography>
                      </Box>

                      <Box display="flex" gap={0.5} flexShrink={0}>
                        <Tooltip title="Editar">
                          <IconButton
                            size="small"
                            onClick={() => handleEditTrigger(trigger.id)}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Remover">
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => handleDeleteClick(trigger.id)}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </Box>
                  </Box>
                );
              })}
            </Stack>
          )}
        </DialogContent>

        <Divider />

        <DialogActions sx={{ px: 3, py: 2, justifyContent: "space-between" }}>
          <Typography variant="caption" color="textSecondary">
            {triggers.length} gatilho{triggers.length !== 1 ? "s" : ""} configurado{triggers.length !== 1 ? "s" : ""}
          </Typography>
          <Box display="flex" gap={1}>
            <Button variant="outlined" onClick={onClose}>
              Fechar
            </Button>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleNewTrigger}
            >
              Novo Gatilho
            </Button>
          </Box>
        </DialogActions>
      </Dialog>

      {/* Modal criar/editar gatilho */}
      <CampaignModalPhrase
        open={phraseModalOpen}
        onClose={handlePhraseClose}
        FlowCampaignId={editingCampaignId}
        onSave={handlePhraseSave}
        fixedFlowId={flowId}
      />

      {/* Confirmação de deleção */}
      <ConfirmationModal
        title="Remover gatilho"
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        onConfirm={handleDeleteConfirm}
      >
        Tem certeza que deseja remover este gatilho? Esta ação não pode ser desfeita.
      </ConfirmationModal>
    </>
  );
};

export default FlowTriggersModal;
